import { gql, useMutation, useQuery } from "@apollo/client";
import { WBAlert, WBButton, WBTextField } from "@apptractive/design-system-web";
import { REGEX } from "@apptractive/stm-shared";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { updateUser as UPDATE_USER } from "../../graphql/mutations";
import { getUser } from "../../graphql/queries";

interface SocialsFormData {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
}

const SocialsForm = () => {
  const { t } = useTranslation();
  const { userId } = useParams();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm<SocialsFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const inputs = useMemo(
    () =>
      ({
        facebook: {
          label: t("facebook", { ns: "common" }),
          name: "facebook",
          type: "url",
          placeholder: "",
          defaultValue: "",
          rules: {
            pattern: {
              value: REGEX.FACEBOOK,
              message: t("facebookInvalid", { ns: "common" }),
            },
          },
        },
        instagram: {
          label: t("instagram", { ns: "common" }),
          name: "instagram",
          type: "url",
          placeholder: "",
          defaultValue: "",
          rules: {
            pattern: {
              value: REGEX.INSTAGRAM,
              message: t("instagram", { ns: "common" }),
            },
          },
        },
        twitter: {
          label: t("twitter", { ns: "common" }),
          name: "twitter",
          type: "url",
          placeholder: "",
          defaultValue: "",
          rules: {
            pattern: {
              value: REGEX.TWITTER,
              message: t("twitterInvalid", { ns: "common" }),
            },
          },
        },
        linkedin: {
          label: t("linkedin", { ns: "common" }),
          name: "linkedin",
          type: "url",
          placeholder: "",
          defaultValue: "",
          rules: {
            pattern: {
              value: REGEX.LINKEDIN,
              message: t("linkedinInvalid", { ns: "common" }),
            },
          },
        },
      } as const),
    [t]
  );

  const { data: userData } = useQuery(gql(getUser), {
    variables: {
      id: userId,
    },
  });

  const [
    updateUser,
    {
      loading: updateLoading,
      data: updateData,
      error: updateError,
      reset: resetUpdate,
    },
  ] = useMutation(gql(UPDATE_USER));

  const user = useMemo(() => userData?.getUser || {}, [userData]);

  useEffect(() => {
    if (user.facebook) {
      setValue("facebook", user.facebook, { shouldValidate: true });
    }
    if (user.instagram) {
      setValue("instagram", user.instagram, { shouldValidate: true });
    }
    if (user.twitter) {
      setValue("twitter", user.twitter, { shouldValidate: true });
    }
    if (user.linkedin) {
      setValue("linkedin", user.linkedin, { shouldValidate: true });
    }
  }, [user, setValue]);

  const onSubmit = async (data: SocialsFormData) => {
    try {
      await updateUser({
        variables: {
          input: {
            ...data,
            id: userId,
          },
        },
      });
    } catch (err) {
      console.log("ERROR updating user: ", err);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name={inputs.facebook.name}
          rules={inputs.facebook.rules}
          defaultValue={inputs.facebook.defaultValue}
          render={({ field }) => (
            <WBTextField
              {...field}
              type={inputs.facebook.type}
              label={inputs.facebook.label}
              //placeholder={inputs.facebook.placeholder}
              error={!!(errors.facebook && errors.facebook.message)}
              helperText={(errors.facebook && errors.facebook.message) || ""}
              margin="dense"
            />
          )}
        />
        <Controller
          control={control}
          name={inputs.instagram.name}
          rules={inputs.instagram.rules}
          defaultValue={inputs.instagram.defaultValue}
          render={({ field }) => (
            <WBTextField
              {...field}
              type={inputs.instagram.type}
              label={inputs.instagram.label}
              //placeholder={inputs.instagram.placeholder}
              error={!!(errors.instagram && errors.instagram.message)}
              helperText={(errors.instagram && errors.instagram.message) || ""}
              margin="dense"
            />
          )}
        />
        <Controller
          control={control}
          name={inputs.twitter.name}
          rules={inputs.twitter.rules}
          defaultValue={inputs.twitter.defaultValue}
          render={({ field }) => (
            <WBTextField
              {...field}
              type={inputs.twitter.type}
              label={inputs.twitter.label}
              //placeholder={inputs.twitter.placeholder}
              error={!!(errors.twitter && errors.twitter.message)}
              helperText={(errors.twitter && errors.twitter.message) || ""}
              margin="dense"
            />
          )}
        />
        <Controller
          control={control}
          name={inputs.linkedin.name}
          rules={inputs.linkedin.rules}
          defaultValue={inputs.linkedin.defaultValue}
          render={({ field }) => (
            <WBTextField
              {...field}
              type={inputs.linkedin.type}
              label={inputs.linkedin.label}
              //placeholder={inputs.linkedin.placeholder}
              error={!!(errors.linkedin && errors.linkedin.message)}
              helperText={(errors.linkedin && errors.linkedin.message) || ""}
              margin="dense"
            />
          )}
        />

        <WBButton sx={{ mt: 1 }} disabled={!isValid} loading={updateLoading}>
          {t("updateTitle", { ns: "common" })}
        </WBButton>
      </form>

      {updateData && (
        <WBAlert
          title={t("profileUpdated", { ns: "user" })}
          severity="success"
          onClose={resetUpdate}
          sx={{ mt: 2 }}
        />
      )}

      {updateError?.message && (
        <WBAlert title={updateError.message} severity="error" sx={{ my: 2 }} />
      )}
    </>
  );
};

export default SocialsForm;
